import { useState } from 'react';
import { useCreateInvoice } from '@/hooks/use-invoices';
import { useProjects } from '@/hooks/use-projects';
import { useClients } from '@/hooks/use-clients';
import { toast } from '@/hooks/use-toast';
import { CatalogueSection } from '@/components/services/catalogue-picker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus, Trash2, Receipt, Send, Landmark, ChevronDown, ChevronUp } from 'lucide-react';

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export function InvoiceDialog() {
  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [clientId, setClientId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [tax, setTax] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [publishNow, setPublishNow] = useState(false);
  const [showBankOverride, setShowBankOverride] = useState(false);
  const [bankAccountName, setBankAccountName] = useState('');
  const [bankSortCode, setBankSortCode] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankPaymentInstructions, setBankPaymentInstructions] = useState('');
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { description: '', quantity: 1, unitPrice: 0, total: 0 },
  ]);

  const { data: projectsData } = useProjects();
  const { data: clientsData } = useClients();
  const createInvoice = useCreateInvoice();

  const projects = projectsData?.data || [];
  const clients = clientsData?.data || [];

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unitPrice: 0, total: 0 }]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length <= 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: string | number) => {
    setLineItems((prev) => {
      const next = [...prev];
      const item = next[index];
      if (field === 'description') {
        next[index] = { ...item, description: String(value) };
      } else if (field === 'quantity') {
        next[index] = { ...item, quantity: Number(value) };
      } else if (field === 'unitPrice') {
        next[index] = { ...item, unitPrice: Number(value) };
      } else if (field === 'total') {
        next[index] = { ...item, total: Number(value) };
      }
      return next;
    });
  };

  const subtotal = lineItems.reduce((sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0), 0);
  const total = subtotal + (tax || 0) - (discount || 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !clientId) return;
    if (!lineItems.some((item) => item.description.trim())) return;

    try {
      const result = await createInvoice.mutateAsync({
        projectId,
        clientId,
        dueDate,
        tax,
        discount,
        status: publishNow ? 'sent' : 'draft',
        lineItems: lineItems
          .filter((item) => item.description.trim())
          .map((item) => ({ ...item, total: item.quantity * item.unitPrice })),
        bankAccountName: bankAccountName.trim() || undefined,
        bankSortCode: bankSortCode.trim() || undefined,
        bankAccountNumber: bankAccountNumber.trim() || undefined,
        bankPaymentInstructions: bankPaymentInstructions.trim() || undefined,
      });

      toast({
        title: 'Invoice created',
        description: publishNow
          ? `Invoice sent for £${total.toFixed(2)} — ${lineItems.length} line item${lineItems.length !== 1 ? 's' : ''}`
          : `Draft saved for £${total.toFixed(2)} — ${lineItems.length} line item${lineItems.length !== 1 ? 's' : ''}`,
        variant: 'success',
      });

      setOpen(false);
      resetForm();
    } catch (err: any) {
      toast({
        title: 'Failed to create invoice',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setProjectId('');
    setClientId('');
    setDueDate('');
    setTax(0);
    setDiscount(0);
    setPublishNow(false);
    setShowBankOverride(false);
    setBankAccountName('');
    setBankSortCode('');
    setBankAccountNumber('');
    setBankPaymentInstructions('');
    setLineItems([{ description: '', quantity: 1, unitPrice: 0, total: 0 }]);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5">
          <Receipt size={14} />
          New Invoice
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-2xl max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Invoice</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <FormField label="Project *">
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Client *">
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.companyName || c.user?.name || c.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </div>

          <FormField label="Due Date">
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </FormField>

          {/* Line items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Line Items</span>
              <div className="flex items-center gap-1.5">
                <CatalogueSection onAddService={(svc) => {
                  setLineItems((prev) => [...prev, {
                    description: svc.name + (svc.description ? ` — ${svc.description}` : ''),
                    quantity: 1,
                    unitPrice: svc.unitPrice,
                    total: svc.unitPrice,
                  }]);
                }} />
                <Button type="button" variant="ghost" size="sm" onClick={addLineItem} className="gap-1">
                  <Plus size={12} />
                  Add Item
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              {lineItems.map((item, index) => (
                <div key={index} className="flex flex-col sm:flex-row items-stretch sm:items-start gap-2 p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
                  <div className="flex-1 space-y-1.5">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2 sm:block">
                    <div className="w-16 sm:w-20 space-y-1.5">
                      <Input
                        type="number"
                        min="1"
                        placeholder="Qty"
                        value={item.quantity || ''}
                        onChange={(e) => updateLineItem(index, 'quantity', Math.max(1, parseInt(e.target.value) || 0))}
                        className="text-sm text-center"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:block">
                    <div className="w-20 sm:w-28 space-y-1.5">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Price"
                        value={item.unitPrice || ''}
                        onChange={(e) => updateLineItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                        className="text-sm text-right"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:block sm:pt-1.5">
                    <div className="w-16 sm:w-20 text-right text-sm font-medium text-[var(--text-secondary)]">
                      £{((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLineItem(index)}
                    disabled={lineItems.length <= 1}
                    className="self-end sm:self-auto sm:pt-1.5 p-1 text-[var(--text-secondary)] hover:text-red-400 transition-colors disabled:opacity-30"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <FormField label="Tax (£)">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={tax || ''}
                onChange={(e) => setTax(parseFloat(e.target.value) || 0)}
              />
            </FormField>
            <FormField label="Discount (£)">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={discount || ''}
                onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
              />
            </FormField>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-end gap-1 sm:gap-4 p-3 rounded-lg bg-[var(--bg-secondary)] text-sm">
            <span className="text-[var(--text-secondary)]">
              Subtotal: <strong className="text-[var(--text-primary)]">£{subtotal.toFixed(2)}</strong>
            </span>
            {tax > 0 && (
              <span className="text-[var(--text-secondary)]">
                Tax: <strong className="text-[var(--text-primary)]">£{tax.toFixed(2)}</strong>
              </span>
            )}
            {discount > 0 && (
              <span className="text-[var(--text-secondary)]">
                Discount: <strong className="text-[var(--text-primary)]">-£{discount.toFixed(2)}</strong>
              </span>
            )}
            <span className="text-base font-bold sm:ml-2">
              Total: £{total.toFixed(2)}
            </span>
          </div>

          {/* Per-invoice bank transfer override (D2 — optional, falls back to global settings) */}
          <div className="rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] overflow-hidden">
            <button
              type="button"
              onClick={() => setShowBankOverride(!showBankOverride)}
              className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium hover:bg-[var(--bg-primary)]/50 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Landmark size={14} className="text-teal-400" />
                Bank transfer details <span className="text-xs text-[var(--text-secondary)] font-normal">(optional override)</span>
              </span>
              {showBankOverride ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showBankOverride && (
              <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FormField label="Account Name">
                  <Input value={bankAccountName} onChange={(e) => setBankAccountName(e.target.value)} placeholder="DynDev Ltd" className="text-sm" />
                </FormField>
                <FormField label="Sort Code">
                  <Input value={bankSortCode} onChange={(e) => setBankSortCode(e.target.value)} placeholder="00-00-00" className="text-sm" />
                </FormField>
                <FormField label="Account Number">
                  <Input value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)} placeholder="00000000" className="text-sm" />
                </FormField>
                <div className="sm:col-span-3">
                  <FormField label="Payment Instructions (optional)">
                    <Input value={bankPaymentInstructions} onChange={(e) => setBankPaymentInstructions(e.target.value)} placeholder="e.g. Please quote the invoice number as the reference" className="text-sm" />
                  </FormField>
                </div>
                <p className="sm:col-span-3 text-xs text-[var(--text-secondary)]">
                  Leave blank to use the bank details configured in Settings → Billing &amp; Terms.
                </p>
              </div>
            )}
          </div>

          {/* Publish toggle + actions */}
          <div className="flex items-center justify-between pt-2">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={publishNow}
                onChange={(e) => setPublishNow(e.target.checked)}
                className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-primary)] text-dyndev-green focus:ring-dyndev-green"
              />
              <span className="text-xs text-[var(--text-secondary)] flex items-center gap-1">
                <Send size={12} />
                Publish and send to client
              </span>
            </label>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createInvoice.isPending || !projectId || !clientId}
              >
                {createInvoice.isPending && <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />}
                {createInvoice.isPending ? 'Creating...' : publishNow ? 'Create & Send' : 'Save as Draft'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
